import { useMemo, useState } from 'react';
import type { Card, Colaborador, Flow } from '../../types';

// 📊 Qualidade das aprovações (14/10/2026, fluxo Compras Revenda do Evandro).
// Cada saída da etapa de aprovação é uma decisão. O etapaHistory guarda o
// camposSnapshot de cada entrada, então a decisão, o valor aprovado e o motivo
// de CADA submissão ficam no histórico (não só a última).
// A aba só aparece em fluxo com campo de lista "Decisão …".

interface Props { flow: Flow; cards: Card[]; colaboradores?: Colaborador[]; onAbrirCard: (card: Card) => void; }

type Tipo = 'integral' | 'parcial' | 'ajuste' | 'reprovada';
interface Decisao { card: Card; n: number; tipo: Tipo; data: Date; horas: number | null; solicitado: number; aprovado: number;
  motivo: string; origem: string; quem: string; comprador: string; fornecedor: string; }

const norm = (s: string) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
export const fluxoTemQualidade = (flow: Flow) =>
  (flow.etapas || []).some(e => (e.campos || []).some(c => c.tipo === 'lista' && /^decis/i.test(norm(c.nome))));

const numero = (v: unknown) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return v;
  const s = String(typeof v === 'object' && v !== null && 'valor' in (v as any) ? (v as any).valor : v).replace(/[^\d,.-]/g, '');
  const n = s.includes(',') ? Number(s.replace(/\./g, '').replace(',', '.')) : Number(s);
  return isNaN(n) ? 0 : n;
};
const texto = (v: unknown) => Array.isArray(v) ? v.join(', ') : v == null ? '' : typeof v === 'object' && 'valor' in (v as any) ? String((v as any).valor) : String(v);
const brl = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const pct = (a: number, b: number) => b ? `${Math.round(a / b * 100)}%` : '—';
const ROT: Record<Tipo, string> = { integral: 'Aprovada integralmente', parcial: 'Aprovada parcialmente', ajuste: 'Devolvida para ajuste', reprovada: 'Reprovada' };
const COR: Record<Tipo, string> = { integral: 'bg-gray-900 text-white', parcial: 'bg-gray-200 text-gray-900', ajuste: 'bg-amber-100 text-amber-800', reprovada: 'bg-red-100 text-red-700' };

export default function QualidadeFluxo({ flow, cards, colaboradores = [], onAbrirCard }: Props) {
  const [dias, setDias] = useState(90);
  const campo = useMemo(() => {
    const todos = (flow.etapas || []).flatMap(e => (e.campos || []).map(c => ({ ...c, etapaId: String(e.id) })));
    const ache = (re: RegExp, tipo?: string) => todos.find(c => re.test(norm(c.nome)) && (!tipo || c.tipo === tipo));
    const dec = ache(/^decis/, 'lista');
    return { dec, aprovEtapa: dec?.etapaId || '', valorAprov: ache(/valor aprovado/), valorProp: ache(/valor proposto/) || ache(/^valor/),
      motivo: ache(/^motivo/), origem: ache(/origem do motivo/), fornecedor: ache(/^fornecedor/), comprador: ache(/comprador/) };
  }, [flow]);
  const nomeEtapa = useMemo(() => new Map((flow.etapas || []).map(e => [String(e.id), norm(e.nome)])), [flow]);
  const nomeDe = (id: string) => colaboradores.find(c => String(c.id) === id)?.nome || '';

  const { decisoes, aguardando } = useMemo(() => {
    const out: Decisao[] = []; let aguardando = 0;
    const desde = Date.now() - dias * 864e5;
    for (const card of cards) {
      const h = ((card as any).etapaHistory || []) as any[];
      if (String(card.etapaId) === campo.aprovEtapa) aguardando++;
      let n = 0;
      h.forEach((e, i) => {
        if (String(e.etapaId) !== campo.aprovEtapa || !h[i + 1]) return;
        const prox = h[i + 1];
        const snap = { ...(card.campos as any), ...(prox.camposSnapshot || {}) };
        const valorDec = norm(texto(campo.dec ? snap[campo.dec.id] : ''));
        const destino = nomeEtapa.get(String(prox.etapaId)) || '';
        const tipo: Tipo = /parcial/.test(valorDec) ? 'parcial' : /integral/.test(valorDec) ? 'integral' : /devolv|ajuste/.test(valorDec) ? 'ajuste'
          : /reprov/.test(valorDec) ? 'reprovada' : /reprov/.test(destino) ? 'reprovada' : /analise|negocia|agenda|cadastro/.test(destino) ? 'ajuste' : 'integral';
        const data = new Date(prox.movedAt || e.leftAt || 0);
        n++;
        if (+data < desde) return;
        const solicitado = numero(campo.valorProp ? snap[campo.valorProp.id] : 0);
        const aprovado = tipo === 'integral' ? (numero(campo.valorAprov ? snap[campo.valorAprov.id] : 0) || solicitado)
          : tipo === 'parcial' ? numero(campo.valorAprov ? snap[campo.valorAprov.id] : 0) : 0;
        const comp = texto(campo.comprador ? snap[campo.comprador.id] : '') || String((card as any).responsavelIndividualId || '');
        out.push({ card, n, tipo, data, solicitado, aprovado,
          horas: e.movedAt ? Math.max(0, (+data - +new Date(e.movedAt)) / 36e5) : null,
          motivo: tipo === 'integral' ? '' : texto(campo.motivo ? snap[campo.motivo.id] : '') || '(sem motivo)',
          origem: tipo === 'integral' ? '' : texto(campo.origem ? snap[campo.origem.id] : ''),
          quem: nomeDe(String(prox.movedBy || '')),
          comprador: nomeDe(comp) || comp || '(sem comprador)',
          fornecedor: texto(campo.fornecedor ? snap[campo.fornecedor.id] : '') || '(sem fornecedor)' });
      });
    }
    return { decisoes: out.sort((a, b) => +b.data - +a.data), aguardando };
  }, [cards, campo, dias, nomeEtapa, colaboradores]); // eslint-disable-line react-hooks/exhaustive-deps

  const primeiras = decisoes.filter(d => d.n === 1);
  const integral1 = primeiras.filter(d => d.tipo === 'integral').length;
  const conta = (t: Tipo) => decisoes.filter(d => d.tipo === t).length;
  const comHoras = decisoes.filter(d => d.horas != null);
  const mediaH = comHoras.length ? comHoras.reduce((a, d) => a + (d.horas || 0), 0) / comHoras.length : 0;
  const aprovadas = decisoes.filter(d => d.tipo === 'integral' || d.tipo === 'parcial');
  const solic = aprovadas.reduce((a, d) => a + d.solicitado, 0), aprov = aprovadas.reduce((a, d) => a + d.aprovado, 0);

  const agrupa = (chave: (d: Decisao) => string) => {
    const m = new Map<string, Decisao[]>();
    decisoes.forEach(d => { const k = chave(d); m.set(k, [...(m.get(k) || []), d]); });
    return [...m.entries()].map(([k, ds]) => {
      const p = ds.filter(d => d.n === 1);
      return { k, total: ds.length, p1: pct(p.filter(d => d.tipo === 'integral').length, p.length),
        ajuste: ds.filter(d => d.tipo === 'ajuste').length, reprov: ds.filter(d => d.tipo === 'reprovada').length,
        cards: new Set(ds.map(d => d.card.id)).size };
    }).sort((a, b) => b.total - a.total);
  };
  const porComprador = agrupa(d => d.comprador), porFornecedor = agrupa(d => d.fornecedor);
  const motivos = (() => {
    const m = new Map<string, { n: number; origens: Map<string, number> }>();
    decisoes.filter(d => d.tipo !== 'integral').forEach(d => d.motivo.split(', ').forEach(mo => {
      const x = m.get(mo) || { n: 0, origens: new Map() }; x.n++; if (d.origem) x.origens.set(d.origem, (x.origens.get(d.origem) || 0) + 1); m.set(mo, x); }));
    return [...m.entries()].sort((a, b) => b[1].n - a[1].n);
  })();
  const maxMot = motivos[0]?.[1].n || 1;

  if (!campo.dec) return <div className="p-8 text-sm text-gray-500">Este fluxo não tem campo de decisão.</div>;

  const Kpi = ({ v, r, d, alerta }: { v: string; r: string; d?: string; alerta?: boolean }) => (
    <div className="bg-white rounded-xl border border-gray-100 px-4 py-3">
      <div className={`text-2xl font-bold tracking-tight ${alerta ? 'text-red-600' : 'text-gray-900'}`}>{v}</div>
      <div className="text-xs text-gray-700 font-medium">{r}</div>{d && <div className="text-[11px] text-gray-500">{d}</div>}
    </div>
  );
  const Tabela = ({ titulo, linhas, rot }: { titulo: string; linhas: ReturnType<typeof agrupa>; rot: string }) => (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-100 text-sm font-semibold">{titulo}</div>
      <table className="w-full text-[13px]">
        <thead><tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 bg-gray-50">
          <th className="px-4 py-2">{rot}</th><th className="px-2 py-2 text-right">Demandas</th><th className="px-2 py-2 text-right">Decisões</th>
          <th className="px-2 py-2 text-right">Integral na 1ª</th><th className="px-2 py-2 text-right">Ajustes</th><th className="px-4 py-2 text-right">Reprov.</th></tr></thead>
        <tbody>{linhas.length ? linhas.map(l => (
          <tr key={l.k} className="border-t border-gray-50"><td className="px-4 py-2 font-medium text-gray-900">{l.k}</td>
            <td className="px-2 py-2 text-right">{l.cards}</td><td className="px-2 py-2 text-right">{l.total}</td><td className="px-2 py-2 text-right">{l.p1}</td>
            <td className={`px-2 py-2 text-right ${l.ajuste ? 'text-amber-700 font-semibold' : ''}`}>{l.ajuste}</td>
            <td className={`px-4 py-2 text-right ${l.reprov ? 'text-red-600 font-semibold' : ''}`}>{l.reprov}</td></tr>))
          : <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">Sem decisões no período.</td></tr>}</tbody>
      </table>
    </div>
  );

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-5 space-y-4">
      <div className="flex items-center gap-3">
        <div><div className="text-lg font-semibold text-gray-900">Qualidade das aprovações</div>
          <div className="text-xs text-gray-500">Cada submissão à {(flow.etapas || []).find(e => String(e.id) === campo.aprovEtapa)?.nome || 'aprovação'} conta como uma decisão</div></div>
        <div className="ml-auto flex gap-1">
          {[30, 90, 365, 3650].map(d => <button key={d} onClick={() => setDias(d)}
            className={`px-3 py-1.5 rounded-lg text-xs border ${dias === d ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-200 text-gray-700'}`}>{d === 3650 ? 'Tudo' : `${d} dias`}</button>)}
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <Kpi v={String(new Set(decisoes.map(d => d.card.id)).size)} r="Demandas avaliadas" d={`${decisoes.length} submissões`} />
        <Kpi v={pct(integral1, primeiras.length)} r="Integral na 1ª" d={`${integral1} de ${primeiras.length} primeiras`} />
        <Kpi v={String(conta('parcial'))} r="Aprovadas parcialmente" />
        <Kpi v={String(conta('ajuste'))} r="Devolvidas para ajuste" alerta={conta('ajuste') > 0} />
        <Kpi v={String(conta('reprovada'))} r="Reprovadas" alerta={conta('reprovada') > 0} />
        <Kpi v={aprov ? `${brl(aprov)}` : '—'} r="Aprovado" d={solic ? `de ${brl(solic)} solicitados (${pct(aprov, solic)})` : undefined} />
        <Kpi v={comHoras.length ? `${mediaH < 24 ? Math.round(mediaH) + ' h' : (mediaH / 24).toFixed(1).replace('.', ',') + ' d'}` : '—'} r="Tempo médio de decisão" d={`${aguardando} aguardando agora`} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Tabela titulo="Por comprador" linhas={porComprador} rot="Comprador" />
        <Tabela titulo="Por fornecedor" linhas={porFornecedor} rot="Fornecedor" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="px-4 py-2.5 border-b border-gray-100 text-sm font-semibold">Motivos de ajuste e reprovação</div>
          <div className="p-4 space-y-2.5">{motivos.length ? motivos.map(([m, x]) => (
            <div key={m}>
              <div className="flex justify-between text-[13px]"><span className="text-gray-900">{m}</span><span className="text-gray-500">{x.n}</span></div>
              <div className="h-2 rounded bg-gray-100 mt-1"><div className="h-2 rounded bg-gray-900" style={{ width: `${x.n / maxMot * 100}%` }} /></div>
              {x.origens.size > 0 && <div className="text-[11px] text-gray-500 mt-0.5">{[...x.origens.entries()].map(([o, n]) => `${o} ${n}`).join(' · ')}</div>}
            </div>)) : <div className="text-sm text-gray-400">Nenhum ajuste ou reprovação no período.</div>}</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="px-4 py-2.5 border-b border-gray-100 text-sm font-semibold">Últimas decisões</div>
          <div className="divide-y divide-gray-50">{decisoes.slice(0, 12).map((d, i) => (
            <button key={i} onClick={() => onAbrirCard(d.card)} className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-3 text-[13px]">
              <span className="text-gray-500 w-20 flex-none">{d.data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} {d.data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
              <span className="flex-1 min-w-0 truncate text-gray-900">{d.card.titulo}{d.n > 1 && <span className="text-gray-500"> · {d.n}ª submissão</span>}</span>
              <span className={`px-2 py-0.5 rounded-full text-[11px] flex-none ${COR[d.tipo]}`}>{ROT[d.tipo]}</span>
              {(d.tipo === 'parcial' || d.tipo === 'integral') && d.aprovado > 0 && <span className="text-gray-700 w-24 text-right flex-none">{brl(d.aprovado)}</span>}
            </button>)) || null}
            {!decisoes.length && <div className="px-4 py-6 text-center text-sm text-gray-400">Sem decisões no período.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
